import { apiClient, apiGet, apiPost, apiPut, apiPatch, apiDelete } from "@/lib/api/client";
import type { PaginatedParams, PaginatedResponse } from "@/lib/api/types";

const BASE = "/admin";

export const adminApi = {
  getOverview: () => apiGet<{ data: Record<string, unknown> }>(`${BASE}/overview`),

  getAnalytics: (entity: string) => apiGet<{ data: Record<string, unknown> }>(`${BASE}/analytics/${entity}`),

  listUsers: (params?: PaginatedParams & { role?: string; search?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", String(params.page));
    if (params?.perPage) searchParams.set("perPage", String(params.perPage));
    if (params?.role) searchParams.set("role", params.role);
    if (params?.search) searchParams.set("search", params.search);
    return apiGet<PaginatedResponse<Record<string, unknown>>>(`${BASE}/users?${searchParams.toString()}`);
  },

  getUser: (id: string) => apiGet<{ data: Record<string, unknown> }>(`${BASE}/users/${id}`),

  changeUserRole: (id: string, role: string) => apiPut<{ data: Record<string, unknown> }>(`${BASE}/users/${id}/role`, { role }),

  suspendUser: (id: string, reason: string) => apiPut<{ data: Record<string, unknown> }>(`${BASE}/users/${id}/suspend`, { reason }),

  restoreUser: (id: string) => apiPut<{ data: Record<string, unknown> }>(`${BASE}/users/${id}/restore`),

  deleteUser: (id: string) => apiDelete<{ data: Record<string, unknown> }>(`${BASE}/users/${id}`),

  verifyUser: (id: string, verified: boolean) => apiPut<{ data: Record<string, unknown> }>(`${BASE}/users/${id}/verify`, { verified }),

  listReports: (params?: PaginatedParams & { status?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", String(params.page));
    if (params?.perPage) searchParams.set("perPage", String(params.perPage));
    if (params?.status) searchParams.set("status", params.status);
    return apiGet<PaginatedResponse<Record<string, unknown>>>(`${BASE}/content/reports?${searchParams.toString()}`);
  },

  resolveReport: (id: string, action: string, note?: string) => apiPost<{ data: Record<string, unknown> }>(`${BASE}/content/reports/${id}/resolve`, { action, note }),

  removeContent: (type: string, id: string, reason?: string) => apiDelete<{ data: Record<string, unknown> }>(`${BASE}/content/${type}/${id}`, { data: { reason } }),

  getSettings: () => apiGet<{ data: Record<string, unknown> }>(`${BASE}/settings`),

  updateSettings: (settings: Record<string, unknown>) => apiPut<{ data: Record<string, unknown> }>(`${BASE}/settings`, settings),

  getAuditLogs: (params?: PaginatedParams & { action?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", String(params.page));
    if (params?.perPage) searchParams.set("perPage", String(params.perPage));
    if (params?.action) searchParams.set("action", params.action);
    return apiGet<PaginatedResponse<Record<string, unknown>>>(`${BASE}/audit-logs?${searchParams.toString()}`);
  },

  bulkDeleteUsers: (userIds: string[]) => apiDelete<{ data: number }>(`${BASE}/users/bulk-delete`, { data: { userIds } }),

  bulkDeleteReports: (reportIds: string[]) => apiDelete<{ data: number }>(`${BASE}/reports/bulk-delete`, { data: { reportIds } }),

  getStats: () => apiGet<{ data: Record<string, unknown> }>(`${BASE}/stats`),

  exportData: (type = "users", format = "CSV") => apiGet<{ data: unknown }>(`${BASE}/export?type=${type}&format=${format}`),

  getEntityRegistry: () => apiGet<{ data: Record<string, unknown>[] }>(`${BASE}/entities/registry`),

  listEntityRows: (slug: string, params?: PaginatedParams & { search?: string; includeDeleted?: boolean }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", String(params.page));
    if (params?.perPage) searchParams.set("perPage", String(params.perPage));
    if (params?.search) searchParams.set("search", params.search);
    if (params?.includeDeleted) searchParams.set("includeDeleted", String(params.includeDeleted));
    return apiGet<PaginatedResponse<Record<string, unknown>>>(`${BASE}/entities/${slug}?${searchParams.toString()}`);
  },

  getEntityRow: (slug: string, id: string) => apiGet<{ data: Record<string, unknown> }>(`${BASE}/entities/${slug}/${id}`),

  updateEntityRow: (slug: string, id: string, body: Record<string, unknown>) => apiPut<{ data: Record<string, unknown> }>(`${BASE}/entities/${slug}/${id}`, body),

  toggleEntityRow: (slug: string, id: string, field: string, value: boolean) => apiPatch<{ data: Record<string, unknown> }>(`${BASE}/entities/${slug}/${id}/toggle`, { field, value }),

  removeEntityRow: (slug: string, id: string) => apiDelete<{ data: Record<string, unknown> }>(`${BASE}/entities/${slug}/${id}`),

  restoreEntityRow: (slug: string, id: string) => apiPost<{ data: Record<string, unknown> }>(`${BASE}/entities/${slug}/${id}/restore`),

  listSessions: (params?: PaginatedParams & { userId?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", String(params.page));
    if (params?.perPage) searchParams.set("perPage", String(params.perPage));
    if (params?.userId) searchParams.set("userId", params.userId);
    return apiGet<PaginatedResponse<Record<string, unknown>>>(`${BASE}/sessions?${searchParams.toString()}`);
  },

  revokeSession: (id: string) => apiDelete<{ message: string }>(`${BASE}/sessions/${id}`),

  revokeAllSessions: (userId: string) => apiPost<{ message: string; count: number }>(`${BASE}/sessions/user/${userId}/revoke-all`),

  getLockouts: (limit?: number) => apiGet<{ data: Record<string, unknown>[] }>(`${BASE}/sessions/lockouts?limit=${limit ?? 100}`),

  clearLockouts: (userId: string) => apiDelete<{ message: string; count: number }>(`${BASE}/sessions/lockouts/user/${userId}`),

  impersonateUser: (userId: string, reason?: string) => apiPost<{ data: Record<string, unknown> }>(`${BASE}/impersonate/${userId}`, { reason }),

  getImpersonationLogs: (params?: PaginatedParams) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", String(params.page));
    if (params?.perPage) searchParams.set("perPage", String(params.perPage));
    return apiGet<PaginatedResponse<Record<string, unknown>>>(`${BASE}/impersonation-logs?${searchParams.toString()}`);
  },
};